import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LOCALES,
  SYNC_INTERVAL_CHOICES,
  SYNC_INTERVAL_DEFAULT_MINUTES,
  type Locale,
  type RangeDays,
  type TenantSettingsUpdate,
} from '@jisy/shared';
import { apiRequest, ApiRequestError } from '../api/client.js';
import {
  fathomConnectionQuery,
  membersQuery,
  settingsQuery,
  useFathomDisconnect,
  useFathomKeySave,
  useLocaleMutation,
} from '../api/queries.js';
import { Avatar } from '../components/Badges.js';
import { currentLocale } from '../i18n/index.js';
import { formatShortDate } from '../lib/format.js';
import styles from './SettingsPage.module.css';

export type SettingsSection = 'integration' | 'meetings' | 'planning' | 'members';

/**
 * Настройки тенанта.
 *
 * Поля Jira выбираются из того, что сайт действительно отдал: список кандидатов приходит
 * из `GET /rest/api/3/field` и отфильтрован по типу — числовые для процента, даты для начала
 * плана. Пока поле не выбрано, значение остаётся в базе Jisy, и это нормальный режим работы.
 */
export function SettingsPage({
  isAdmin,
  initialSection = 'integration',
  onClose,
}: {
  isAdmin: boolean;
  /** С какого раздела открыться: из встреч ведут прямо к ключу Fathom. */
  initialSection?: SettingsSection;
  onClose: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [section, setSection] = useState<SettingsSection>(initialSection);

  const settings = useQuery(settingsQuery);
  const members = useQuery(membersQuery);
  const localeMutation = useLocaleMutation();

  const save = useMutation({
    mutationFn: (patch: TenantSettingsUpdate) =>
      apiRequest<{ settings: unknown }>('/api/settings', { method: 'PUT', body: patch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      void queryClient.invalidateQueries({ queryKey: ['account'] });
    },
  });

  const disconnect = useMutation({
    mutationFn: () => apiRequest<{ plansKept: number }>('/api/integration/jira', { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.clear();
      window.location.assign('/connect');
    },
  });

  const data = settings.data;
  const saveError = save.error instanceof ApiRequestError ? save.error : null;

  return (
    <div className={styles.page}>
      <nav className={styles.sidebar} aria-label={t('settings.title')}>
        <div className={`label ${styles.sidebarTitle}`}>{t('settings.title')}</div>
        {(['integration', 'meetings', 'planning', 'members'] as const).map((key) => (
          <button
            type="button"
            key={key}
            className={section === key ? styles.navItemActive : styles.navItem}
            onClick={() => setSection(key)}
          >
            {t(`settings.${key}`)}
          </button>
        ))}
        <div className={styles.sidebarSpacer} />
        <button type="button" className={styles.backButton} onClick={onClose}>
          {t('settings.back')}
        </button>
      </nav>

      <main className={styles.content}>
        {section === 'integration' && (
          <>
            <header className={styles.head}>
              <h2 className={styles.title}>{t('settings.integration')}</h2>
              <p className={styles.subtitle}>
                {data?.connection.syncSourceName
                  ? t('settings.connectedBy', {
                      name: data.connection.syncSourceName,
                      date: formatShortDate(data.connection.connectedAt),
                    })
                  : t('settings.notConnected')}
              </p>
            </header>

            {data?.fieldsError && (
              <p className={styles.warning} role="status">
                {t('settings.fieldsUnavailable')}
              </p>
            )}

            <FieldSelect
              label={t('settings.progressField')}
              hint={t('settings.progressFieldHint')}
              value={data?.settings.progressFieldId ?? ''}
              options={data?.fields.progress ?? []}
              disabled={!isAdmin || save.isPending}
              onChange={(value) => save.mutate({ progressFieldId: value })}
            />

            <FieldSelect
              label={t('settings.startDateField')}
              hint={t('settings.startDateFieldHint')}
              value={data?.settings.startDateFieldId ?? ''}
              options={data?.fields.startDate ?? []}
              disabled={!isAdmin || save.isPending}
              onChange={(value) => save.mutate({ startDateFieldId: value })}
            />

            {/*
              Порог, после которого кэш задач считается устаревшим. Не расписание: обновление
              ставится при обращении к таблице, поэтому пока никто не работает, Jira не трогаем.
            */}
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="sync-interval">
                {t('settings.syncInterval')}
              </label>
              <select
                id="sync-interval"
                className={styles.select}
                value={data?.settings.syncIntervalMinutes ?? SYNC_INTERVAL_DEFAULT_MINUTES}
                disabled={!isAdmin || save.isPending}
                onChange={(event) =>
                  save.mutate({ syncIntervalMinutes: Number(event.target.value) })
                }
              >
                {SYNC_INTERVAL_CHOICES.map((minutes) => (
                  <option value={minutes} key={minutes}>
                    {intervalLabel(minutes, t)}
                  </option>
                ))}
              </select>
              <span className={styles.fieldHint}>{t('settings.syncIntervalHint')}</span>
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="write-to-jira">
                {t('settings.writeToJira')}
              </label>
              <div className={styles.toggleRow}>
                <input
                  id="write-to-jira"
                  type="checkbox"
                  className={styles.checkbox}
                  checked={data?.settings.writeToJira ?? true}
                  disabled={!isAdmin || save.isPending}
                  onChange={(event) => save.mutate({ writeToJira: event.target.checked })}
                />
                <span className={styles.fieldHint}>{t('settings.writeToJiraHint')}</span>
              </div>
            </div>

            <div className={styles.dangerZone}>
              <div className={styles.dangerText}>
                <span className={styles.dangerTitle}>{t('settings.disconnect')}</span>
                <span className={styles.fieldHint}>{t('settings.disconnectHint')}</span>
              </div>
              <button
                type="button"
                className={styles.dangerButton}
                disabled={!isAdmin || disconnect.isPending}
                onClick={() => {
                  if (window.confirm(t('settings.disconnectConfirm'))) disconnect.mutate();
                }}
              >
                {t('settings.disconnectAction')}
              </button>
            </div>
          </>
        )}

        {section === 'meetings' && <FathomSection />}

        {section === 'planning' && (
          <>
            <header className={styles.head}>
              <h2 className={styles.title}>{t('settings.planning')}</h2>
              <p className={styles.subtitle}>{t('settings.planningSubtitle')}</p>
            </header>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('settings.defaultRange')}</span>
              <div className={styles.segmented}>
                {([7, 14] as RangeDays[]).map((value) => (
                  <button
                    type="button"
                    key={value}
                    className={
                      data?.settings.defaultRangeDays === value
                        ? styles.segmentActive
                        : styles.segment
                    }
                    disabled={!isAdmin || save.isPending}
                    onClick={() => save.mutate({ defaultRangeDays: value })}
                  >
                    {t(value === 7 ? 'plan.days7' : 'plan.days14')}
                  </button>
                ))}
              </div>
              <span className={styles.fieldHint}>{t('settings.defaultRangeHint')}</span>
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('settings.language')}</span>
              <div className={styles.segmented}>
                {LOCALES.map((locale) => (
                  <button
                    type="button"
                    key={locale}
                    className={currentLocale() === locale ? styles.segmentActive : styles.segment}
                    onClick={() => localeMutation.mutate(locale as Locale)}
                  >
                    {t(`locales.${locale}`)}
                  </button>
                ))}
              </div>
              <span className={styles.fieldHint}>{t('settings.languageHint')}</span>
            </div>
          </>
        )}

        {section === 'members' && (
          <>
            <header className={styles.head}>
              <h2 className={styles.title}>{t('settings.members')}</h2>
              <p className={styles.subtitle}>{t('settings.membersSubtitle')}</p>
            </header>

            <div className={styles.memberList}>
              {(members.data?.members ?? []).map((member) => (
                <div className={styles.member} key={member.accountId}>
                  <Avatar user={member} size={26} />
                  <span className={`${styles.memberName} truncate`}>{member.displayName}</span>
                  <span className={styles.memberTags}>
                    {member.isAppUser ? (
                      <span className={styles.tag}>
                        {member.role === 'admin' ? t('settings.roleAdmin') : t('settings.roleMember')}
                      </span>
                    ) : (
                      <span className={styles.tagMuted}>{t('settings.notSignedIn')}</span>
                    )}
                    {member.hasPlan && <span className={styles.tagAccent}>{t('settings.hasPlan')}</span>}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Ключ Fathom — личная настройка, роль на неё не влияет: предупреждение было бы ложью. */}
        {!isAdmin && section !== 'meetings' && (
          <p className={styles.warning}>{t('settings.adminOnly')}</p>
        )}

        {saveError && (
          <p className={styles.error} role="alert">
            {t(`errors.${saveError.code}`)}
          </p>
        )}
      </main>
    </div>
  );
}

/**
 * Ключ Fathom. Настройка личная, а не тенанта: ключ даёт доступ ровно к тем встречам, которые
 * записал его владелец, поэтому роль администратора здесь ничего не решает.
 *
 * Введённый ключ на экран больше не возвращается — сервер отдаёт только признак подключения.
 * Поэтому поле после сохранения очищается: показывать в нём звёздочки вместо значения,
 * которого у клиента нет, значило бы притворяться, что ключ можно посмотреть.
 */
function FathomSection(): React.ReactElement {
  const { t } = useTranslation();
  const [key, setKey] = useState('');

  const connection = useQuery(fathomConnectionQuery);
  const save = useFathomKeySave();
  const disconnect = useFathomDisconnect();

  const data = connection.data;
  const error = save.error instanceof ApiRequestError ? save.error : null;

  return (
    <>
      <header className={styles.head}>
        <h2 className={styles.title}>{t('settings.meetings')}</h2>
        <p className={styles.subtitle}>
          {data?.connected
            ? t('settings.fathomConnectedAt', { date: formatShortDate(data.connectedAt) })
            : t('settings.fathomNotConnected')}
        </p>
      </header>

      {data?.revoked && <p className={styles.warning}>{t('settings.fathomRevoked')}</p>}

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="fathom-key">
          {t('settings.fathomKey')}
        </label>
        <input
          id="fathom-key"
          className={styles.input}
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={key}
          placeholder={t('settings.fathomKeyPlaceholder')}
          onChange={(event) => setKey(event.target.value)}
        />
        <span className={styles.fieldHint}>
          {t('settings.fathomKeyHint')}{' '}
          <a
            className={styles.link}
            href="https://fathom.video/settings/api"
            target="_blank"
            rel="noreferrer noopener"
          >
            {t('settings.fathomKeyLink')}
          </a>
        </span>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primaryButton}
          disabled={key.trim().length < 8 || save.isPending}
          onClick={() =>
            save.mutate(key.trim(), {
              onSuccess: () => {
                setKey('');
              },
            })
          }
        >
          {save.isPending ? t('settings.fathomChecking') : t('settings.fathomSave')}
        </button>

        {data?.connected === true || data?.revoked === true ? (
          <button
            type="button"
            className={styles.dangerButton}
            disabled={disconnect.isPending}
            onClick={() => {
              if (window.confirm(t('settings.fathomDisconnectConfirm'))) disconnect.mutate();
            }}
          >
            {t('settings.fathomDisconnect')}
          </button>
        ) : null}
      </div>

      {error && (
        <p className={styles.error} role="alert">
          {t(`errors.${error.code}`)}
        </p>
      )}
    </>
  );
}

function FieldSelect({
  label,
  hint,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  options: Array<{ id: string; name: string; custom: boolean }>;
  disabled: boolean;
  onChange: (value: string | null) => void;
}): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <select
        className={styles.select}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
      >
        <option value="">{t('settings.fieldNotChosen')}</option>
        {options.map((field) => (
          <option value={field.id} key={field.id}>
            {field.name}
            {field.custom ? ` · ${field.id}` : ''}
          </option>
        ))}
      </select>
      <span className={styles.fieldHint}>{hint}</span>
    </div>
  );
}

/**
 * Подпись интервала. Крупные значения читаются часами и сутками: «раз в 1440 минут» —
 * технически верно и неудобочитаемо. Склонение делает i18next, ему нужен именно `count`.
 */
function intervalLabel(minutes: number, t: (key: string, options: { count: number }) => string): string {
  if (minutes % (24 * 60) === 0) return t('settings.syncEveryDays', { count: minutes / (24 * 60) });
  if (minutes % 60 === 0) return t('settings.syncEveryHours', { count: minutes / 60 });
  return t('settings.syncEveryMinutes', { count: minutes });
}
